/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AllIcon from '@material-ui/icons/FontDownload';
import { 
  EpDevPSearchContextValue, 
  useEpDevPSearch 
} from '../../context';
import { 
  EpDevPSearchDocumentType 
} from './types';
import { useServiceRegistryTranslation } from '../../../../../i18n/ServiceRegistryLanguageProvider';


const useStyles = makeStyles(theme => ({
  card: {
    backgroundColor: 'rgba(0, 0, 0, .11)',
  },
  cardContent: {
    paddingTop: theme.spacing(1),
  },
  icon: {
    color: theme.palette.common.black,
  },
  list: {
    width: '100%',
  },
  listItemIcon: {
    width: '24px',
    height: '24px',
  },
  accordion: {
    backgroundColor: theme.palette.background.paper,
  },
  accordionSummary: {
    minHeight: 'auto',
    '&.Mui-expanded': {
      minHeight: 'auto',
    },
  },
  accordionSummaryContent: {
    margin: theme.spacing(2, 0),
    '&.Mui-expanded': {
      margin: theme.spacing(2, 0),
    },
  },
  accordionDetails: {
    padding: theme.spacing(0, 0, 1),
  },
}));


export interface IEpDevPSearchDocumentTypesAccordionProps {
  title?: string;
  documentTypes: Array<EpDevPSearchDocumentType>;
};

export const EpDevPSearchDocumentTypesAccordion = (props: IEpDevPSearchDocumentTypesAccordionProps) => {
  const { t } = useServiceRegistryTranslation();

  const classes = useStyles();
  const useSearchContext: EpDevPSearchContextValue = useEpDevPSearch();
  const [expanded, setExpanded] = React.useState<boolean>(true);
  const [selectedDocumentTypeId, setSelectedDocumentTypeId] = React.useState<string>('');
  
  const toggleExpanded = () => setExpanded(prevState => !prevState);

  const handleClick = (documentTypeId: string) => {
    
    return () => {
      useSearchContext.setDocumentTypes(documentTypeId !== '' ? [documentTypeId] : ['ep-devp-consumer-apps','ep-devp-consumer-products']);
      useSearchContext.setPageNumber(1);
      setSelectedDocumentTypeId(documentTypeId);
      setExpanded(false);
    };
  };

  const definedDocumentTypes: Array<EpDevPSearchDocumentType> = [
    {
      id: '',
      displayName: 'All',
      icon: <AllIcon />,
    },
    ...props.documentTypes,
  ];

  const getDocumentTypeDisplayName = (documentTypeId: string): string | undefined => {
    return definedDocumentTypes.find( x => x.id === documentTypeId )?.displayName;
  }

  return (
    <Card className={classes.card}>
      <CardHeader title={props.title} titleTypographyProps={{ variant: 'overline' }} />
      <CardContent className={classes.cardContent}>
        <Accordion
          className={classes.accordion}
          expanded={expanded}
          onChange={toggleExpanded}
        >
          <AccordionSummary
            classes={{
              root: classes.accordionSummary,
              content: classes.accordionSummaryContent,
            }}
            expandIcon={<ExpandMoreIcon className={classes.icon} />}
            IconButtonProps={{ size: 'small' }}
          >
            {expanded 
              ? t('developerSearch.collapse')
              : getDocumentTypeDisplayName(selectedDocumentTypeId)
            }
          </AccordionSummary>
          <AccordionDetails classes={{ root: classes.accordionDetails }}>
            <List
              className={classes.list}
              component="nav"
              aria-label={t('developerSearch.filterByType')}
              disablePadding
              dense
            >
              {definedDocumentTypes.map(documentType => (
                <React.Fragment key={documentType.id}>
                  <Divider />
                  <ListItem
                    selected={documentType.id === selectedDocumentTypeId}
                    onClick={handleClick(documentType.id)}
                    button
                  >
                    <ListItemIcon>
                      {React.cloneElement(documentType.icon, {
                        className: classes.listItemIcon,
                      })}
                    </ListItemIcon>
                    <ListItemText primary={documentType.displayName} />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};
